import {PepPublicRole} from '@sollapay/enums';
import {
  FieldDatePicker,
  FieldInput,
  FieldSelect,
  FieldTextarea,
  InfoTooltip
} from '@sollapay/ui/components';
import {useMemo} from 'react';
import {Controller, useWatch} from 'react-hook-form';
import {useTranslation} from 'react-i18next';

import {PepYesNoField} from './PepYesNoField';

import type {FieldValues, Control, UseFormRegister} from 'react-hook-form';

const PUBLIC_ROLE_OPTIONS = Object.values(PepPublicRole);

interface PepPartyFieldsProps<T extends FieldValues> {
  partyPath: string;
  isCorporate: boolean;
  control: Control<T>;
  register: UseFormRegister<T>;
  fe: (key: string) => string | undefined;
  /** When false, native RHF validation rules are omitted so partial saves are not blocked. Defaults to true. */
  enforceRequired?: boolean;
}

export function PepPartyFields<T extends FieldValues>({
  partyPath,
  isCorporate,
  control,
  register,
  fe,
  enforceRequired = true
}: PepPartyFieldsProps<T>) {
  const {t} = useTranslation('trustAccounts');
  const p = `activation.packages.trustDetails.form.fields.pep` as const;
  const op = `activation.packages.trustDetails.form.options` as const;

  const required = enforceRequired ? t('common:required') : undefined;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const c = control as unknown as Control<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const r = register as UseFormRegister<any>;

  const stillInRole = useWatch({control: c, name: `${partyPath}.stillInRole`});
  const roleStartMonth = useWatch({control: c, name: `${partyPath}.roleStartMonth`});

  const publicRoleOptions = useMemo(
    () =>
      PUBLIC_ROLE_OPTIONS.map(role => ({value: role, label: t(`${op}.pepPublicRoles.${role}`)})),
    [t, op]
  );

  const publicRoleField = (
    <Controller
      name={`${partyPath}.publicRole` as never}
      control={c}
      rules={required ? {required} : undefined}
      render={({field}) => (
        <FieldSelect
          label={
            <>
              {t(`${p}.publicRole`)}
              <InfoTooltip content={t(`${p}.publicRoleTooltip`)} />
            </>
          }
          required
          placeholder={t(`${p}.publicRolePlaceholder`)}
          error={fe(`${partyPath}.publicRole`)}
          value={field.value}
          onValueChange={field.onChange}
          options={publicRoleOptions}
        />
      )}
    />
  );

  const countryOfOfficeField = (
    <FieldInput
      label={t(`${p}.countryOfOffice`)}
      required
      placeholder={t(`${p}.countryOfOfficePlaceholder`)}
      error={fe(`${partyPath}.countryOfOffice`)}
      {...r(`${partyPath}.countryOfOffice`, required ? {required} : {})}
    />
  );

  const stillInRoleField = (
    <Controller
      name={`${partyPath}.stillInRole` as never}
      control={c}
      render={({field}) => (
        <PepYesNoField
          label={t(`${p}.stillInRole`)}
          value={!!field.value}
          onChange={field.onChange}
        />
      )}
    />
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-2 gap-6">
        {isCorporate ? (
          <>
            <FieldInput
              label={t(`${p}.fullName`)}
              required
              placeholder={t(`${p}.fullNamePlaceholder`)}
              error={fe(`${partyPath}.fullName`)}
              {...r(`${partyPath}.fullName`, required ? {required} : {})}
            />
            {countryOfOfficeField}
            {publicRoleField}
            {stillInRoleField}
          </>
        ) : (
          <>
            {publicRoleField}
            {countryOfOfficeField}
            <div className="col-span-2">{stillInRoleField}</div>
          </>
        )}

        <Controller
          name={`${partyPath}.roleStartMonth` as never}
          control={c}
          rules={required ? {required} : undefined}
          render={({field}) => (
            <FieldDatePicker
              monthOnly
              label={t(`${p}.roleStartMonth`)}
              required
              placeholder="MM/YYYY"
              value={field.value ?? ''}
              onChange={field.onChange}
              onBlur={field.onBlur}
              error={fe(`${partyPath}.roleStartMonth`)}
            />
          )}
        />

        {!stillInRole && (
          <Controller
            name={`${partyPath}.roleEndMonth` as never}
            control={c}
            rules={
              required
                ? {
                    required,
                    validate: (v: string) =>
                      !v ||
                      !roleStartMonth ||
                      v >= roleStartMonth ||
                      t('activation.packages.trustDetails.form.errors.roleEndMonthBeforeStart')
                  }
                : undefined
            }
            render={({field}) => (
              <FieldDatePicker
                monthOnly
                label={t(`${p}.roleEndMonth`)}
                required
                placeholder="MM/YYYY"
                value={field.value ?? ''}
                onChange={field.onChange}
                onBlur={field.onBlur}
                error={fe(`${partyPath}.roleEndMonth`)}
              />
            )}
          />
        )}

        <div className="col-span-2">
          <FieldTextarea
            label={t(`${p}.sourceOfWealth`)}
            required
            placeholder={t(`${p}.sourceOfWealthPlaceholder`)}
            error={fe(`${partyPath}.sourceOfWealth`)}
            {...r(`${partyPath}.sourceOfWealth`, required ? {required} : {})}
          />
        </div>
      </div>
    </div>
  );
}
